import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  TableHead,
  IconButton,
  Collapse,
  Divider,
  Dialog,
  DialogContent,
  Button,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import authService from "../Auth/AuthService";
import {
  AccountCircle,
  AddCircle,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Subscriptions,
  Refresh as RefreshIcon,
  LocalOffer,
  Discount,
  Delete,
} from "@mui/icons-material";
import AddSucriptionForm from "../subscription/AddSucriptionForm";

const LoginDetailView = (roles) => {
  const [loginData, setLoginData] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false);
  const [openRow, setOpenRow] = useState({});
  const [open, setOpen] = useState(false);
  const [selectedLoginId, setSelectedLoginId] = useState(null);
  const [preSelectedCouponCode, setPreSelectedCouponCode] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const url = authService.pathUrl("GET", "/auth/login");
      const res = await authService.getAuth(url);
      setLoginData(res);
      setLoading(false);
    } catch (e) {
      console.error("Error fetching order:", e);
      setLoading(false);
    }
  };

  const toggleRow = (id) => {
    setOpenRow((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const openAddSubscription = (login, coupon) => {
    setSelectedLoginId({ id: login.id, paidby: roles.user });
    setPreSelectedCouponCode(coupon);
    setOpen(true);
  };

  const isCouponValid = (userCoupon) => {
    if (userCoupon.isUsed) return false;
    const today = new Date().toISOString().split("T")[0];
    const endDate = userCoupon.endDate || userCoupon.coupon?.endDate;
    if (endDate && endDate < today) return false;
    const startDate = userCoupon.startDate || userCoupon.coupon?.startDate;
    if (startDate && startDate > today) return false;
    return true;
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleRefreshAll = () => {
    loadData();
    showSnackbar('Refreshing all data...', 'info');
  };

  const mobileVerify = async (login) => {
    const data = {
      ...login,
      isMobileVerify: true
    };
    try {
      const url = authService.pathUrl("PUT", "/auth/admin/verify-otp/" + login.id);
      const res = await authService.putAuth(url, data);
      setLoading(false);
      loadData();
    } catch (e) {
      console.error("Error fetching order:", e);
      setLoading(false);
    }
  }

  const deleteLogin = async (loginid) => {
    try {
      const url = authService.pathUrl("DELETE", "/auth/" + loginid);
      await authService.deleteAuth(url);
      setLoading(false);
      loadData();
    } catch (e) {
      console.error("Error delete login :", e);
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!loginData) {
    return (
      <Typography color="error" align="center" sx={{ mt: 5 }}>
        Login details not found
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ maxWidth: "95%", mx: "auto", mt: 4 }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h5" sx={{ p: 2 }} color="#3f2d01" fontWeight={"bold"}>
          <AccountCircle sx={{ mr: 1 }} />
          Login Details
        </Typography>
        <Tooltip title="Refresh All Data">
          <IconButton onClick={handleRefreshAll} color="primary" disabled={loading.dashboard}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider sx={{ mb: 3 }} />
      <Table>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>ID</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Mobile</TableCell>
            <TableCell>Role Type</TableCell>
            <TableCell>Login Type</TableCell>
            <TableCell>Master Code</TableCell>
            <TableCell>Mobile Verify</TableCell>
            <TableCell>Email Verify</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.isArray(loginData) &&
            loginData?.map((login) => (
              <React.Fragment key={login.id}>
                <TableRow>
                  <TableCell>
                    {(login.subscipe?.length > 0 || login.userCoupons?.length > 0) && (
                      <IconButton size="small" onClick={() => toggleRow(login.id)}>
                        {openRow[login.id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>{login.id}</TableCell>
                  <TableCell>{login.email}</TableCell>
                  <TableCell>{login.mobile}</TableCell>
                  <TableCell>{login.roleType}</TableCell>
                  <TableCell>{login.loginType}</TableCell>
                  <TableCell>{login.masterCode || "-"}</TableCell>
                  <TableCell
                    onClick={() => {
                      if (!login?.isMobileVerify) {
                        mobileVerify(login);
                      }
                    }}
                    xs={{ cursor: login?.isMobileVerify ? 'default' : 'pointer' }}
                  >
                    {login?.isMobileVerify ? "Yes" : "No"}
                  </TableCell>
                  <TableCell>{login?.isEmailVerify ? "Yes" : "No"}</TableCell>
                  <TableCell>{login.isActive ? "Yes" : "No"}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => deleteLogin(login.id)}
                      title="Delete Login"
                      size="small"
                    >
                      <Delete sx={{ border: "2px solid", borderRadius: "10px", color: 'red' }} fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>

                {/* Expandable Row for Subscriptions & Coupons */}
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                    <Collapse in={openRow[login.id]} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1 }}>
                        {/* Subscriptions Section */}
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="subtitle1" gutterBottom color="#3f2d01" fontWeight={"bold"}>
                            <Subscriptions sx={{ mr: 1 }} />
                            Subscriptions
                          </Typography>
                          <Typography variant="h5" gutterBottom fontWeight="bold" onClick={() => openAddSubscription(login)}>
                            <AddCircle
                              sx={{ mr: 1, verticalAlign: "middle", color: "#1b1914", cursor: "pointer" }}
                            />
                          </Typography>
                        </Box>
                        <Divider sx={{ mb: 3 }} />
                        {login.subscipe?.length > 0 ? (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Plan Name</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Duration (Days)</TableCell>
                                <TableCell>Coupon Code</TableCell>
                                <TableCell>Start Date</TableCell>
                                <TableCell>End Date</TableCell>
                                <TableCell>Active</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {login.subscipe.map((sub) => (
                                <TableRow key={sub.id}>
                                  <TableCell>{sub.id}</TableCell>
                                  <TableCell>{sub.subscriptionPlan.name}</TableCell>
                                  <TableCell>{sub.subscriptionPlan.price}</TableCell>
                                  <TableCell>{sub.subscriptionPlan.durationInDays}</TableCell>
                                  <TableCell>{sub.couponCode || "-"}</TableCell>
                                  <TableCell>{sub.startDate}</TableCell>
                                  <TableCell>{sub.endDate}</TableCell>
                                  <TableCell>{sub.isActive ? "Yes" : "No"}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <Typography variant="body2" sx={{ mb: 2 }}>
                            No active subscriptions.
                          </Typography>
                        )}

                        {/* User Coupons Section */}
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          color="#3f2d01"
                          fontWeight={"bold"}
                          sx={{ mt: 3 }}
                        >
                          <LocalOffer sx={{ mr: 1 }} />
                          Referral Coupons
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {login.userCoupons?.length > 0 ? (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Coupon Code</TableCell>
                                <TableCell>Discount</TableCell>
                                {login.userCoupons.coupon?.discountAmount != null &&
                                  <TableCell>Discount Amount</TableCell>
                                }
                                <TableCell>Valid From</TableCell>
                                <TableCell>Valid To</TableCell>
                                <TableCell>Is Used</TableCell>
                                <TableCell>Used At</TableCell>
                                <TableCell>Action</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {login.userCoupons.map((uc) => {
                                const valid = isCouponValid(uc);
                                const couponCode = uc.coupon?.code;
                                return (
                                  <TableRow key={uc.id}>
                                    <TableCell>{couponCode || "-"}</TableCell>
                                    <TableCell>
                                      {uc.coupon?.discountPercentage != null
                                        ? `${uc.coupon.discountPercentage}%`
                                        : "-"}
                                    </TableCell>
                                    {uc.coupon?.discountAmount != null &&
                                      <TableCell>{uc.coupon.discountAmount}</TableCell>
                                    }
                                    <TableCell>{uc.startDate || uc.coupon?.startDate || "-"}</TableCell>
                                    <TableCell>{uc.endDate || uc.coupon?.endDate || "-"}</TableCell>
                                    <TableCell>{uc.isUsed ? "Yes" : "No"}</TableCell>
                                    <TableCell>{uc.usedAt || "-"}</TableCell>
                                    <TableCell>
                                      {valid && couponCode && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<Discount />}
                                          onClick={() => openAddSubscription(login, uc)}
                                          sx={{ textTransform: "none" }}
                                        >
                                          Apply
                                        </Button>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        ) : (
                          <Typography variant="body2" sx={{ mb: 2 }}>
                            No coupons assigned.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 0 }}>
          {open && (
            <AddSucriptionForm
              loginId={selectedLoginId}
              coupon={preSelectedCouponCode}
              onSuccess={() => { setOpen(false); loadData(); }}
            />
          )}
        </DialogContent>
      </Dialog>
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </TableContainer>
  );
};

export default LoginDetailView;